import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2, Play, Pause, Scissors, AlertTriangle, Music2 } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../config';

const MIN_SEGMENT = 1; // secondes - largeur mini du segment
const EDGE_PX = 14; // zone de saisie (pixels) autour de chaque poignee gauche/droite

function formatTime(s) {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export default function AudioTrimModal({ file, maxDuration, onConfirm, onCancel }) {
  const token = localStorage.getItem('auth_token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [localUrl] = useState(() => URL.createObjectURL(file));
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [drag, setDrag] = useState(null); // { mode: 'start'|'end'|'move', anchor } | null
  const [previewing, setPreviewing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState(''); // 'upload' | 'cut'
  const [uploadPct, setUploadPct] = useState(0);
  const [error, setError] = useState('');

  const audioRef = useRef(null);
  const timelineRef = useRef(null);

  useEffect(() => () => URL.revokeObjectURL(localUrl), [localUrl]);

  const onLoadedMetadata = () => {
    const d = audioRef.current?.duration || 0;
    const segLen = maxDuration ? Math.min(maxDuration, d) : d;
    setDuration(d);
    setStart(0);
    setEnd(segLen);
    setReady(true);
  };

  const posToTime = (clientX) => {
    const rect = timelineRef.current.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    return Math.min(Math.max(ratio, 0), 1) * duration;
  };

  const onTimelineMouseDown = (e) => {
    if (!ready) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const startPx = (start / duration) * rect.width;
    const endPx = (end / duration) * rect.width;
    const clickPx = e.clientX - rect.left;

    if (Math.abs(clickPx - startPx) <= EDGE_PX) {
      setDrag({ mode: 'start', anchor: 0 });
    } else if (Math.abs(clickPx - endPx) <= EDGE_PX) {
      setDrag({ mode: 'end', anchor: 0 });
    } else if (clickPx >= startPx && clickPx <= endPx) {
      setDrag({ mode: 'move', anchor: posToTime(e.clientX) - start });
    } else {
      const t = posToTime(e.clientX);
      const maxLen = maxDuration || duration;
      const len = Math.min(end - start, maxLen, duration);
      const newStart = Math.min(Math.max(0, t - len / 2), Math.max(0, duration - len));
      setStart(newStart);
      setEnd(newStart + len);
      if (audioRef.current) audioRef.current.currentTime = newStart;
      setDrag({ mode: 'move', anchor: len / 2 });
    }
  };

  useEffect(() => {
    if (!drag) return undefined;
    const onMove = (e) => {
      const t = posToTime(e.clientX);
      const maxLen = maxDuration || duration;
      if (drag.mode === 'start') {
        const floor = Math.max(0, end - maxLen);
        const newStart = Math.min(Math.max(floor, t), end - MIN_SEGMENT);
        setStart(newStart);
        if (audioRef.current) audioRef.current.currentTime = newStart;
      } else if (drag.mode === 'end') {
        const ceil = Math.min(duration, start + maxLen);
        const newEnd = Math.max(Math.min(ceil, t), start + MIN_SEGMENT);
        setEnd(newEnd);
        if (audioRef.current) audioRef.current.currentTime = newEnd;
      } else if (drag.mode === 'move') {
        const len = end - start;
        const newStart = Math.min(Math.max(0, t - drag.anchor), duration - len);
        setStart(newStart);
        setEnd(newStart + len);
        if (audioRef.current) audioRef.current.currentTime = newStart;
      }
    };
    const onUp = () => setDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag, start, end, duration, maxDuration]);

  const togglePreview = () => {
    const a = audioRef.current;
    if (!a || !ready) return;
    if (previewing) {
      a.pause();
      setPreviewing(false);
      return;
    }
    a.currentTime = start;
    a.play();
    setPreviewing(true);
  };

  const onTimeUpdate = () => {
    const a = audioRef.current;
    if (!a || !previewing) return;
    if (a.currentTime >= end) {
      a.pause();
      setPreviewing(false);
    }
  };

  // Reessaie l'upload en cas de coupure reseau (ecran verrouille, changement d'appli, micro-
  // coupure mobile) - meme logique/raison que VideoTrimModal.uploadWithRetry. Ne reessaie QUE
  // les echecs reseau (pas de reponse serveur), pas une vraie erreur API.
  const uploadWithRetry = async (formData, maxRetries = 3) => {
    for (let attempt = 0; ; attempt++) {
      try {
        return await axios.post(`${API_URL}/api/ia-studio/audio/trim/upload`, formData, {
          headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt) => {
            // Plafonne a 100% - meme garde-fou que VideoTrimModal (reseau mobile instable).
            if (evt.total) setUploadPct(Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
          },
        });
      } catch (e) {
        if (e.response || attempt >= maxRetries) throw e;
        setProcessingStep('retry');
        setUploadPct(0);
        await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
        setProcessingStep('upload');
      }
    }
  };

  const confirmTrim = async () => {
    if (processing || !ready) return;
    setError('');
    setProcessing(true);
    setUploadPct(0);
    try {
      setProcessingStep('upload');
      const formData = new FormData();
      formData.append('audio', file);
      const uploadRes = await uploadWithRetry(formData);
      if (!uploadRes.data.success) throw new Error(uploadRes.data.error || "Échec de l'upload");
      const { filename } = uploadRes.data;

      setProcessingStep('cut');
      const cutDuration = end - start;
      const cutRes = await axios.post(`${API_URL}/api/ia-studio/audio/trim/cut`, {
        filename, start, duration: cutDuration,
      }, { headers: authHeaders });
      if (!cutRes.data.success) throw new Error(cutRes.data.error || 'Échec du découpage');

      const blob = await (await fetch(cutRes.data.url)).blob();
      const baseName = file.name.replace(/\.[^./\\]+$/, '');
      const trimmedFile = new File([blob], `trimmed_${baseName}.mp3`, { type: blob.type || 'audio/mpeg' });
      onConfirm(trimmedFile, cutRes.data.duration);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Une erreur est survenue.');
      setProcessing(false);
      setProcessingStep('');
    }
  };

  const windowLeftPct = duration ? (start / duration) * 100 : 0;
  const windowWidthPct = duration ? ((end - start) / duration) * 100 : 100;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ animation: 'teckia-trim-fade 0.2s ease' }}
      onClick={() => !processing && onCancel()}
    >
      <div
        className="relative bg-[#140a24] border border-purple-500/30 rounded-2xl max-w-lg w-full p-5 shadow-[0_0_60px_rgba(147,51,234,0.25)]"
        style={{ animation: 'teckia-trim-scale 0.2s ease' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white font-bold text-base flex items-center gap-2">
            <span className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777)' }}>
              <Scissors className="w-4 h-4 text-white" />
            </span>
            Découper l'audio
          </h3>
          <button
            onClick={() => !processing && onCancel()}
            disabled={processing}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 flex items-center justify-center transition"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        <p className="text-gray-400 text-xs mb-4">
          {maxDuration ? `Choisissez librement le début et la fin (max ${formatTime(maxDuration)}).` : 'Choisissez librement le début et la fin.'} Glissez les poignées ou déplacez tout le segment.
        </p>

        <div className="relative rounded-xl overflow-hidden border border-purple-500/25 bg-gradient-to-br from-purple-950/40 to-fuchsia-950/40 mb-3 h-24 flex items-center justify-center">
          <audio
            ref={audioRef}
            src={localUrl}
            onLoadedMetadata={onLoadedMetadata}
            onTimeUpdate={onTimeUpdate}
            onPause={() => setPreviewing(false)}
            className="hidden"
          />
          <Music2 className={`w-10 h-10 text-purple-300/60 ${previewing ? 'animate-pulse' : ''}`} />
          {!ready && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <Loader2 className="w-6 h-6 text-purple-300 animate-spin" />
            </div>
          )}
        </div>

        <div
          ref={timelineRef}
          onMouseDown={onTimelineMouseDown}
          className={`relative h-8 rounded-full border border-white/10 bg-white/5 select-none ${ready ? 'cursor-pointer' : 'opacity-40 pointer-events-none'}`}
        >
          <div className="absolute inset-y-0 my-auto h-1.5 left-2 right-2 bg-white/10 rounded-full" />
          <div
            className={`absolute top-0.5 bottom-0.5 rounded-full bg-gradient-to-r from-purple-500 to-fuchsia-500 border-2 border-white/50 shadow-[0_0_16px_rgba(147,51,234,0.6)] ${drag?.mode === 'move' ? 'cursor-grabbing' : 'cursor-grab'}`}
            style={{ left: `${windowLeftPct}%`, width: `${windowWidthPct}%` }}
          >
            <div className={`absolute -left-1 top-0 bottom-0 w-3 rounded-full bg-white/90 shadow ${drag?.mode === 'start' ? 'cursor-grabbing' : 'cursor-ew-resize'}`} />
            <div className={`absolute -right-1 top-0 bottom-0 w-3 rounded-full bg-white/90 shadow ${drag?.mode === 'end' ? 'cursor-grabbing' : 'cursor-ew-resize'}`} />
          </div>
        </div>
        <div className="flex items-center justify-between text-xs mt-2 mb-4">
          <span className="text-purple-300 font-bold">
            {formatTime(start)} → {formatTime(end)} <span className="text-purple-300/60 font-normal">({formatTime(end - start)})</span>
          </span>
          <span className="text-gray-500">sur {formatTime(duration)}</span>
        </div>

        <button
          onClick={togglePreview}
          disabled={!ready}
          className="w-full mb-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-40 text-white text-sm font-semibold flex items-center justify-center gap-2 transition"
        >
          {previewing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          {previewing ? 'Pause' : 'Écouter'}
        </button>

        {processing && (processingStep === 'upload' || processingStep === 'retry') && (
          <div className="mb-4 flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-xs text-amber-300">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Restez sur cette page jusqu'à la fin de l'envoi - changer d'appli ou éteindre l'écran peut interrompre l'upload.</span>
          </div>
        )}

        {error && (
          <div className="mb-4 flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-xs text-red-300">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={() => !processing && onCancel()}
            disabled={processing}
            className="flex-1 py-3 rounded-xl font-bold text-sm text-gray-300 bg-white/5 hover:bg-white/10 disabled:opacity-40 transition"
          >
            Annuler
          </button>
          <button
            onClick={confirmTrim}
            disabled={!ready || processing}
            className="flex-1 py-3 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-40 transition hover:scale-[1.01]"
            style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777)' }}
          >
            {processing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {processingStep === 'upload' ? `Envoi... ${uploadPct}%`
                  : processingStep === 'retry' ? 'Reconnexion...' : 'Découpage...'}
              </>
            ) : (
              <>
                <Scissors className="w-4 h-4" /> Confirmer le découpage
              </>
            )}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes teckia-trim-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes teckia-trim-scale { from { opacity: 0; transform: scale(0.96) } to { opacity: 1; transform: scale(1) } }
      `}</style>
    </div>
  );
}
